import React, { useCallback, useMemo, useState } from 'react';
import { MotionConfig, motion } from 'framer-motion';
import { LuCalendarRange, LuFileSpreadsheet } from 'react-icons/lu';
import PageHero from '../../components/data/PageHero';
import Button from '../../components/ui/Button';
import { fadeUp, staggerContainer } from '../../components/ui/motionPresets';
import { useUserProfile } from '../../contexts/UserProfileContext';
import useAllowedEmployeeIds from '../../hooks/useAllowedEmployeeIds';
import { useTranslation } from '../../i18n/LanguageContext';
import VisitModal from './VisitModal';
import SCTrackingTable from './SCTrackingTable';
import CascadeDeleteDialog from './operations/CascadeDeleteDialog';
import DetailedReportDialog from './operations/DetailedReportDialog';
import JobDetailsDialog from './operations/JobDetailsDialog';
import OperationsCalendar from './operations/OperationsCalendar';
import PersonnelReports from './operations/PersonnelReports';
import useOperationsActions from './operations/useOperationsActions';
import useOperationsData from './operations/useOperationsData';
import usePersonnelStatistics from './operations/usePersonnelStatistics';
// Only global theme variables are left in this file; importing it keeps their place in the cascade
import './OperationManagementPage.module.css';
// Global legacy dark-mode rules on generic class names (.tooltip, .dateInput, ...); kept so other screens look as before
import './OperationManagementDarkMode.css';

/**
 * Operasyon Yönetimi: seçilen çalışanın takvimi (profil takvimiyle aynı
 * kayıtlar ve aynı görünüm; Ay / Hafta / Gün / Liste), Personel Raporları,
 * SC Takip Tablosu ve tüm çalışanlar için Detaylı Rapor (Excel).
 */
const OperationManagementPage = () => {
  const { employeeId, userEmail, userAuthorization } = useUserProfile();
  const { t, language } = useTranslation();
  const label = (key, vars) => t(`operations.${key}`, vars);

  // Check if user is a restricted user (can only view and delete)
  const isRestrictedUser = userEmail === 'turanemre1999@gmail.com';

  // Çalışan Filtresi visibility tiers:
  //  - admin            → all employees (no restriction)
  //  - SMO (owns centers, like in SMO Lead Reports) → only their workers
  //  - Region Manager (manages regions) → workers in their region's centers
  //  - everyone else    → only themselves
  // userAuthorization may be an array or a single string — normalize to an array.
  const authList = Array.isArray(userAuthorization)
    ? userAuthorization
    : (userAuthorization ? [userAuthorization] : []);
  const isAdmin = authList.includes('admin');

  // Set of employee IDs the current user is allowed to filter by.
  // null = no restriction (admin sees everyone).
  const allowedEmployeeIds = useAllowedEmployeeIds(isAdmin, employeeId);

  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month');
  const [showPersonnelReports, setShowPersonnelReports] = useState(false);

  const errorText = useMemo(() => ({ load: t('operations.toast.load'), loadAll: t('operations.toast.loadAll') }), [t]);
  const data = useOperationsData({ currentDate, selectedEmployeeId, showPersonnelReports, errorText });
  const { employees, projects } = data;

  // Memoize employee and project lookup maps for O(1) access instead of O(n) find()
  const employeeMap = useMemo(() => {
    const map = new Map();
    employees.forEach(emp => map.set(emp.id, emp));
    return map;
  }, [employees]);

  const projectMap = useMemo(() => {
    const map = new Map();
    projects.forEach(proj => map.set(proj.id, proj));
    return map;
  }, [projects]);

  // Employees shown in the Çalışan Filtresi dropdown, scoped to what the
  // current user is allowed to see (admin → all, SMO Lead → their workers,
  // Region Manager → workers in their region, everyone else → only themselves).
  const filterableEmployees = useMemo(() => {
    // While a restricted user's allowed set is still being computed, show
    // nothing rather than briefly exposing every employee.
    if (!isAdmin && !allowedEmployeeIds) return [];
    return employees
      .filter(emp => emp.Active === 1)
      .filter(emp => !allowedEmployeeIds || allowedEmployeeIds.has(emp.id))
      .sort((a, b) =>
        `${a.eName} ${a.eSurname}`.localeCompare(
          `${b.eName} ${b.eSurname}`, 'tr', { sensitivity: 'base' }
        )
      );
  }, [employees, allowedEmployeeIds, isAdmin]);

  const getEmployeeName = useCallback((empId) => {
    const employee = employeeMap.get(empId);
    return employee ? `${employee.eName} ${employee.eSurname}` : '';
  }, [employeeMap]);

  const getProjectName = useCallback((projectId) => {
    const project = projectMap.get(projectId);
    return project ? (project.titleShort || project.projectName || '') : '';
  }, [projectMap]);

  const reportText = useMemo(() => ({
    noData: t('operations.toast.noData'),
    exported: t('operations.toast.personnelExported'),
    exportError: t('operations.toast.exportError'),
  }), [t]);
  const report = usePersonnelStatistics({ allVisits: data.allVisits, allCalendarJobs: data.allCalendarJobs, employees, text: reportText });

  const actionText = useMemo(() => ({
    jobDone: t('operations.toast.jobDone'),
    jobOpen: t('operations.toast.jobOpen'),
    jobUpdateError: t('operations.toast.jobUpdateError'),
    jobDeleted: t('operations.toast.jobDeleted'),
    jobDeleteError: t('operations.toast.jobDeleteError'),
    cascadeNoEmployee: t('operations.toast.cascadeNoEmployee'),
    cascadeNoPatient: t('operations.toast.cascadeNoPatient'),
    cascadeNoTitle: t('operations.toast.cascadeNoTitle'),
    cascadeNoDate: t('operations.toast.cascadeNoDate'),
    cascadeListError: t('operations.toast.cascadeListError'),
    mirrorError: t('operations.toast.mirrorError'),
    cascadeDeleted: (count) => t('operations.toast.cascadeDeleted', { count }),
    cascadeDeleteError: t('operations.toast.cascadeDeleteError'),
    reportDates: t('operations.toast.reportDates'),
    reportDone: (visits, jobs) => t('operations.toast.reportDone', { visits, jobs }),
    reportError: t('operations.toast.reportError'),
  }), [t]);
  const actions = useOperationsActions({
    isAdmin,
    employees,
    getProjectName,
    currentDate,
    selectedEmployeeId,
    data,
    text: actionText,
  });

  // Navigation for week view
  const handlePrevWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentDate(newDate);
  };

  const handleNextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentDate(newDate);
  };

  // Navigation for day view
  const handlePrevDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 1);
    setCurrentDate(newDate);
  };

  const handleNextDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 1);
    setCurrentDate(newDate);
  };

  const handlePrevMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1)
    );
  };

  const handleNextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1)
    );
  };

  // Handle navigation based on view mode
  const handlePrev = () => {
    switch (viewMode) {
      case 'week':
        handlePrevWeek();
        break;
      case 'day':
        handlePrevDay();
        break;
      default:
        handlePrevMonth();
    }
  };

  const handleNext = () => {
    switch (viewMode) {
      case 'week':
        handleNextWeek();
        break;
      case 'day':
        handleNextDay();
        break;
      default:
        handleNextMonth();
    }
  };

  const handleTodayClick = () => {
    setCurrentDate(new Date());
  };

  const closeJob = () => {
    actions.setShowJobDeleteModal(false);
    actions.setSelectedJob(null);
  };

  return (
    <>
      <MotionConfig reducedMotion="user">
        <motion.div
          lang={language}
          variants={staggerContainer(0.06)}
          initial="hidden"
          animate="show"
          className="tw-mx-auto tw-w-full tw-max-w-[1680px] tw-space-y-4 tw-font-sans tw-antialiased sm:tw-space-y-5 md:tw-px-4 lg:tw-px-6 2xl:tw-px-10"
        >
          <motion.div variants={fadeUp}>
            <PageHero
              icon={LuCalendarRange}
              gradient="tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30"
              title={label('title')}
              subtitle={label('subtitle')}
            >
              <Button onClick={() => actions.setShowDateRangePicker(true)} title={label('report.buttonHint')} className="max-sm:tw-w-full">
                <LuFileSpreadsheet aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-emerald-600 dark:tw-text-emerald-400" />
                {label('report.button')}
              </Button>
            </PageHero>
          </motion.div>

          <motion.div variants={fadeUp}>
            <OperationsCalendar
              employees={filterableEmployees}
              selectedEmployee={selectedEmployeeId ? employeeMap.get(selectedEmployeeId) || null : null}
              onSelectEmployee={setSelectedEmployeeId}
              visits={data.visits}
              calendarJobs={data.calendarJobs}
              leaveDayMap={data.leaveDayMap}
              employeeMap={employeeMap}
              projects={projects}
              getProjectName={getProjectName}
              isLoading={data.isLoading}
              currentDate={currentDate}
              setCurrentDate={setCurrentDate}
              viewMode={viewMode}
              setViewMode={setViewMode}
              onPrev={handlePrev}
              onNext={handleNext}
              onToday={handleTodayClick}
              onRefresh={data.refresh}
              onOpenVisit={actions.handleVisitClick}
              onOpenJob={actions.handleJobClick}
            />
          </motion.div>

          <motion.div variants={fadeUp}>
            <PersonnelReports
              open={showPersonnelReports}
              onToggle={() => setShowPersonnelReports(!showPersonnelReports)}
              isLoadingAllData={data.isLoadingAllData}
              allDataLoaded={data.allDataLoaded}
              activeCount={employees.filter(e => e.Active === 1).length}
              report={report}
              getProjectName={getProjectName}
            />
          </motion.div>

          <motion.div variants={fadeUp}>
            <SCTrackingTable />
          </motion.div>
        </motion.div>
      </MotionConfig>

      <DetailedReportDialog
        isOpen={actions.showDateRangePicker}
        startDate={actions.startDate}
        endDate={actions.endDate}
        onStartDate={actions.setStartDate}
        onEndDate={actions.setEndDate}
        onGenerate={actions.handleGenerateReport}
        onClose={() => actions.setShowDateRangePicker(false)}
      />

      <VisitModal
        show={actions.showVisitModal}
        onHide={() => actions.setShowVisitModal(false)}
        visit={actions.selectedVisit}
        onVisitUpdate={actions.handleVisitUpdate}
        onCascadeDelete={isAdmin ? (visit) => actions.buildCascadePreview(visit, 'visit') : undefined}
      />

      <JobDetailsDialog
        isOpen={actions.showJobDeleteModal && Boolean(actions.selectedJob)}
        job={actions.selectedJob}
        employeeName={actions.selectedJob ? getEmployeeName(actions.selectedJob.employeeId) : ''}
        isRestrictedUser={isRestrictedUser}
        isAdmin={isAdmin}
        isDeleting={actions.isDeletingJob}
        isBuildingCascade={actions.isBuildingCascade}
        onToggleStatus={actions.handleToggleJobStatus}
        onDelete={actions.handleDeleteJob}
        onCascadeDelete={() => actions.buildCascadePreview(actions.selectedJob, 'job')}
        onClose={closeJob}
      />

      {/* Cascade Delete Preview — admin only */}
      <CascadeDeleteDialog
        cascade={isAdmin ? actions.cascade : null}
        isBuilding={actions.isBuildingCascade}
        isDeleting={actions.isCascadeDeleting}
        employeeNameOf={getEmployeeName}
        projectNameOf={getProjectName}
        onConfirm={actions.handleConfirmCascadeDelete}
        onClose={() => actions.setCascade(null)}
      />
    </>
  );
};

export default OperationManagementPage;
